// ft3d-hud/warning-mirror.ts — keeps the ~69 features that write to #div_sidelog_warning visible.
//
// On FT3D routes the legacy panel stays MOUNTED but is not presented, so a feature that calls
// openSideLog() and writes its spinner/notice into #div_sidelog_warning would otherwise write into
// an invisible node. Mirroring is deliberately one-way and read-only: openSideLog() itself is left
// alone, so every non-FT3D route behaves exactly as before.

const TOAST_MS = 6000;
const LOADING_TEXT = /^loading(?:\.{3}|\u2026)?$/i;
const ACTION_BAR_GAP_PX = 12;

function mountNoticeAnchor(host: HTMLElement): void {
    const dock = document.querySelector<HTMLElement>('[data-ft3d-actor-dock]');
    if (!dock) {
        host.dataset.ft3dToastAnchor = 'lower-center';
        return;
    }

    let frame = 0;
    const sync = (): void => {
        frame = 0;
        const rect = dock.getBoundingClientRect();
        const visible = !dock.hidden
            && rect.width > 0
            && rect.height > 0
            && window.getComputedStyle(dock).display !== 'none';
        if (!visible) {
            host.style.removeProperty('--ft3d-toast-bottom');
            host.dataset.ft3dToastAnchor = 'lower-center';
            return;
        }

        // The fold-outs are normal-flow siblings inside this bottom-anchored dock, so rect.top is
        // the top of whichever combination is currently open. Clamp the offset so an unusually tall
        // tray still leaves the notification visibly inside the viewport.
        const bottom = Math.min(
            Math.max(ACTION_BAR_GAP_PX, window.innerHeight - rect.top + ACTION_BAR_GAP_PX),
            Math.max(ACTION_BAR_GAP_PX, window.innerHeight - 64),
        );
        host.style.setProperty('--ft3d-toast-bottom', `${Math.ceil(bottom)}px`);
        host.dataset.ft3dToastAnchor = 'action-bar';
    };
    const schedule = (): void => {
        if (frame !== 0) return;
        frame = window.requestAnimationFrame(sync);
    };

    window.addEventListener('resize', schedule, { passive: true });
    if (typeof ResizeObserver === 'function') {
        try { new ResizeObserver(schedule).observe(dock); } catch { /* safe fixed-position fallback */ }
    }
    if (typeof MutationObserver === 'function') {
        try {
            new MutationObserver(schedule).observe(dock, {
                attributes: true,
                attributeFilter: ['hidden', 'style', 'class', 'data-ft-bar-size'],
            });
        } catch { /* safe fixed-position fallback */ }
    }
    sync();
}

function syncNoticeTone(host: HTMLElement, source: HTMLElement): void {
    const success = source.querySelector('.bg-success, .alert-success') !== null;
    host.classList.toggle('is-success', success);
}

function isSpinnerOnlyNotice(source: HTMLElement, text: string): boolean {
    return LOADING_TEXT.test(text)
        && source.querySelector('[data-ft-fade-skip="1"] .spinner-border') !== null;
}

export function mountFt3dWarningMirror(root: HTMLElement): void {
    const host = root.querySelector<HTMLElement>('[data-ft3d-toast]');
    const source = document.getElementById('div_sidelog_warning');
    if (!host || !source || typeof MutationObserver !== 'function') return;

    mountNoticeAnchor(host);

    let timer: number | undefined;

    const show = (): void => {
        const text = (source.textContent || '').replace(/\s+/g, ' ').trim();
        if (timer !== undefined) {
            window.clearTimeout(timer);
            timer = undefined;
        }
        if (text === '') {
            host.hidden = true;
            host.classList.remove('is-loading', 'is-success');
            host.textContent = '';
            return;
        }

        if (isSpinnerOnlyNotice(source, text)) {
            const spinner = document.createElement('span');
            spinner.className = 'ft3d-hud__spinner';
            spinner.setAttribute('aria-hidden', 'true');

            const accessibleLabel = document.createElement('span');
            accessibleLabel.className = 'visually-hidden';
            accessibleLabel.textContent = 'Loading';

            host.classList.remove('is-success');
            host.classList.add('is-loading');
            host.replaceChildren(spinner, accessibleLabel);
            host.hidden = false;
            return;
        }

        host.classList.remove('is-loading');
        syncNoticeTone(host, source);
        host.textContent = text;
        host.hidden = false;
        timer = window.setTimeout(() => {
            host.hidden = true;
            host.classList.remove('is-loading', 'is-success');
            host.textContent = '';
            timer = undefined;
        }, TOAST_MS);
    };

    new MutationObserver(show).observe(source, { childList: true, subtree: true, characterData: true });
    show();
}
